import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatDateTime } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Bookings.jsx.
interface FacilityOption {
  id: number;
  name: string;
}

interface BookingRow {
  id: number;
  title: string;
  booked_by: string | null;
  start_at: string;
  end_at: string;
  status: string;
  facility: { name: string } | null;
}

export function Bookings() {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facility_id') ?? '';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);

  useEffect(() => {
    api.get<{ data: FacilityOption[] }>('/facilities', { params: { per_page: 100 } }).then((res) => setFacilities(res.data.data));
  }, []);

  const filters = useMemo(
    () => ({ search: search || undefined, status: status || undefined, facility_id: facilityId || undefined }),
    [search, status, facilityId]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<BookingRow>('/bookings', filters);

  const remove = async (booking: BookingRow) => {
    if (!(await confirmDelete(`Delete booking "${booking.title}"?`))) return;
    try {
      await api.delete(`/bookings/${booking.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<BookingRow>(
      api,
      '/bookings',
      { search: search || undefined, status: status || undefined, facility_id: facilityId || undefined },
      [
        { label: 'Facility', value: (r) => r.facility?.name ?? '' },
        { label: 'Title', value: (r) => r.title },
        { label: 'Booked By', value: (r) => r.booked_by ?? '' },
        { label: 'Start', value: (r) => r.start_at },
        { label: 'End', value: (r) => r.end_at },
        { label: 'Status', value: (r) => labelFor(lists, 'booking_status', r.status) },
      ],
      'bookings.csv'
    )
  );

  const facilityName = facilities.find((f) => String(f.id) === facilityId)?.name;

  return (
    <>
      <header className="page-header">
        <h1>Bookings</h1>
        <Link
          className="btn btn-primary"
          to={facilityId ? `/resources/bookings/new?facility_id=${facilityId}` : '/resources/bookings/new'}
        >
          + Add booking
        </Link>
      </header>
      <p className="muted">
        {facilityId ? `Bookings for ${facilityName ?? 'this facility'}.` : 'Reservations across all facilities.'}
      </p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search title or booked by…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {listOptions(lists, 'booking_status').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Facility</th>
                <th>Title</th>
                <th>Booked By</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((b) => (
                  <tr key={b.id}>
                    <td>{b.facility?.name ?? '—'}</td>
                    <td>
                      <strong>{b.title}</strong>
                    </td>
                    <td className="muted">{b.booked_by || '—'}</td>
                    <td>{formatDateTime(b.start_at)}</td>
                    <td>{formatDateTime(b.end_at)}</td>
                    <td>
                      <span className={`badge badge-${b.status === 'confirmed' ? 'normal' : 'important'}`}>
                        {labelFor(lists, 'booking_status', b.status)}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/resources/bookings/${b.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/resources/bookings/${b.id}/edit`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(b)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No bookings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
